import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as REPointerEvent,
} from 'react';
import type { LauncherPosition } from '@teo/widget-shared';
import { LauncherButton } from '../components/LauncherButton.js';
import {
  clamp,
  DRAG_STORAGE_PREFIX,
  expandedLauncherPos,
  originStorageSuffix,
  viewportSize,
  type LauncherRect,
  type Point,
} from './launcher-geometry.js';

const DRAG_THRESHOLD_PX = 8;

type DraggableLauncherProps = {
  position: LauncherPosition;
  sizePx: number;
  offsetX: number;
  offsetY: number;
  unreadCount?: number;
  onOpen: () => void;
  /** Called whenever the launcher rect changes (for greeting anchor). */
  onRectChange?: (rect: LauncherRect) => void;
  ariaLabel?: string;
};

function clampPos(p: Point, sizePx: number): Point {
  const { w, h } = viewportSize();
  return {
    x: clamp(p.x, 4, w - sizePx - 4),
    y: clamp(p.y, 4, h - sizePx - 4),
  };
}

function readStored(
  position: LauncherPosition,
  sizePx: number,
  offsetX: number,
  offsetY: number,
): Point {
  try {
    const raw = localStorage.getItem(DRAG_STORAGE_PREFIX + originStorageSuffix());
    if (!raw) return clampPos(expandedLauncherPos(position, sizePx, offsetX, offsetY), sizePx);
    const parsed = JSON.parse(raw) as Point & { position?: string };
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') {
      return clampPos(expandedLauncherPos(position, sizePx, offsetX, offsetY), sizePx);
    }
    return clampPos(parsed, sizePx);
  } catch {
    return clampPos(expandedLauncherPos(position, sizePx, offsetX, offsetY), sizePx);
  }
}

export function DraggableLauncher({
  position,
  sizePx,
  offsetX,
  offsetY,
  unreadCount = 0,
  onOpen,
  onRectChange,
  ariaLabel,
}: DraggableLauncherProps) {
  const [pos, setPos] = useState<Point>(() =>
    readStored(position, sizePx, offsetX, offsetY),
  );
  const posRef = useRef(pos);
  posRef.current = pos;

  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);

  const emitRect = useCallback(
    (p: Point) => {
      onRectChange?.({ x: p.x, y: p.y, w: sizePx, h: sizePx });
    },
    [onRectChange, sizePx],
  );

  useEffect(() => {
    emitRect(pos);
  }, [pos, emitRect]);

  useEffect(() => {
    const onResize = () => {
      setPos((p) => {
        const next = clampPos(p, sizePx);
        posRef.current = next;
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [sizePx]);

  const persist = useCallback(
    (next: Point) => {
      const clamped = clampPos(next, sizePx);
      setPos(clamped);
      posRef.current = clamped;
      try {
        localStorage.setItem(
          DRAG_STORAGE_PREFIX + originStorageSuffix(),
          JSON.stringify({ ...clamped, position }),
        );
      } catch {
        /* ignore */
      }
      emitRect(clamped);
    },
    [emitRect, position, sizePx],
  );

  const onPointerDown = (e: REPointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: posRef.current.x,
      origY: posRef.current.y,
      moved: false,
    };
  };

  const onPointerMove = (e: REPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    d.moved = true;
    const next = clampPos({ x: d.origX + dx, y: d.origY + dy }, sizePx);
    setPos(next);
    posRef.current = next;
    emitRect(next);
  };

  const endPointer = (e: REPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (d.moved) {
      persist(posRef.current);
      return;
    }
    onOpen();
  };

  return (
    <div
      className="teo-mobile-drag-launcher"
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: sizePx,
        height: sizePx,
        zIndex: 2147483100,
        touchAction: 'none',
        pointerEvents: 'auto',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
    >
      <LauncherButton
        unreadCount={unreadCount}
        onClick={() => {
          /* open handled by pointer end when not dragged */
        }}
        ariaLabel={ariaLabel}
      />
    </div>
  );
}
